import { useCallback, useEffect, useRef, useState } from "react";

import type {
  AdminDanceIdentity,
  AdminDailyFamilyPlays,
  AdminDanceSummary,
  AdminDanceRetention,
  AdminDashboardData,
  AdminFamilyDances
} from "@recital-master/shared";

import { useAuthState } from "@/auth/AuthProvider";
import {
  getAdminDanceRetention,
  getAdminDailyFamilyPlays,
  getAdminDashboard,
  getAdminFamilyDances
} from "@/firebase/callables";
import {
  sampleAdminDanceRetention,
  sampleAdminDailyFamilyPlays,
  sampleAdminDashboard,
  sampleAdminFamilyDances
} from "@/data/adminDashboardSample";

export type AdminDataHookResult<T> = {
  loading: boolean;
  error: Error | null;
  data: T | null;
  refresh: () => Promise<void>;
};

export function useAdminDashboard(): AdminDataHookResult<AdminDashboardData> {
  const { devMode, demoMode, isAdmin } = useAuthState();
  // Local design preview: no-config dev, or dev-build demo browsing. A store
  // build's demo mode never previews — __DEV__ is false there.
  const preview = devMode || (__DEV__ && demoMode);
  const [data, setData] = useState<AdminDashboardData | null>(
    preview ? sampleAdminDashboard : null
  );
  const [loading, setLoading] = useState(!preview && isAdmin);
  const [error, setError] = useState<Error | null>(null);
  const refreshInFlight = useRef(false);
  const refreshQueued = useRef(false);

  const refresh = useCallback(async () => {
    if (preview) {
      setData(sampleAdminDashboard);
      setError(null);
      return;
    }
    if (!isAdmin) {
      setData(null);
      setLoading(false);
      return;
    }
    if (refreshInFlight.current) {
      refreshQueued.current = true;
      return;
    }
    refreshInFlight.current = true;
    setLoading(true);
    setError(null);
    try {
      do {
        refreshQueued.current = false;
        setData(await getAdminDashboard());
      } while (refreshQueued.current);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError : new Error(String(nextError)));
    } finally {
      refreshInFlight.current = false;
      setLoading(false);
    }
  }, [preview, isAdmin]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { loading, error, data, refresh };
}

export function useAdminDailyFamilyPlays(
  familyAccountId: string,
  date: string,
  fallbackDances: AdminDanceSummary[] = []
): AdminDataHookResult<AdminDailyFamilyPlays> {
  const {devMode, demoMode, isAdmin} = useAuthState();
  const preview = devMode || (__DEV__ && demoMode);
  const [data, setData] = useState<AdminDailyFamilyPlays | null>(() =>
    preview ? sampleAdminDailyFamilyPlays(date, familyAccountId, fallbackDances) : null
  );
  const [loading, setLoading] = useState(!preview && isAdmin);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    if (preview) {
      setData(sampleAdminDailyFamilyPlays(date, familyAccountId, fallbackDances));
      setError(null);
      setLoading(false);
      return;
    }
    if (!isAdmin) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setData(await getAdminDailyFamilyPlays(familyAccountId, date));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError : new Error(String(nextError)));
    } finally {
      setLoading(false);
    }
  }, [date, fallbackDances, familyAccountId, isAdmin, preview]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {loading, error, data, refresh};
}

export function useAdminDanceRetention(
  dance: AdminDanceIdentity | null,
  familyAccountId?: string
): AdminDataHookResult<AdminDanceRetention> {
  const { devMode, demoMode, isAdmin } = useAuthState();
  const preview = devMode || (__DEV__ && demoMode);
  const [data, setData] = useState<AdminDanceRetention | null>(() =>
    preview && dance ? sampleAdminDanceRetention(dance, familyAccountId) : null
  );
  const [loading, setLoading] = useState(!preview && Boolean(dance) && isAdmin);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    if (!dance) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    if (preview) {
      setData(sampleAdminDanceRetention(dance, familyAccountId));
      setError(null);
      return;
    }
    if (!isAdmin) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setData(await getAdminDanceRetention(dance, familyAccountId));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError : new Error(String(nextError)));
    } finally {
      setLoading(false);
    }
  }, [dance, familyAccountId, preview, isAdmin]);

  useEffect(() => {
    setData(null);
    void refresh();
  }, [refresh]);

  return { loading, error, data, refresh };
}

export function useAdminFamilyDances(
  familyAccountId: string | null,
  refreshKey?: string
): AdminDataHookResult<AdminFamilyDances> {
  const { devMode, demoMode, isAdmin } = useAuthState();
  const preview = devMode || (__DEV__ && demoMode);
  const [data, setData] = useState<AdminFamilyDances | null>(() =>
    preview && familyAccountId ? sampleAdminFamilyDances(familyAccountId) : null
  );
  const [loading, setLoading] = useState(!preview && Boolean(familyAccountId) && isAdmin);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    if (!familyAccountId) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    if (preview) {
      setData(sampleAdminFamilyDances(familyAccountId));
      setError(null);
      return;
    }
    if (!isAdmin) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setData(await getAdminFamilyDances(familyAccountId));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError : new Error(String(nextError)));
    } finally {
      setLoading(false);
    }
  }, [familyAccountId, preview, isAdmin, refreshKey]);

  useEffect(() => {
    setData(null);
    void refresh();
  }, [refresh]);

  return { loading, error, data, refresh };
}
